import { add as addDate, getISODay, setISODay, sub as subDate } from 'date-fns';

import { fetchWithToken } from '@adsk/acs-sc-web-platform-api';

import { weekOffsetMap } from './consts';
import {
  ActivityRow,
  IsoWeekday,
  PaginatedResults,
  QuickLookFilters,
} from '../types';

export interface PaginationOptions {
  limit?: number;
  offset?: number;
}

export function createQueryStringFromObject(obj: never) {
  if (!Object.keys(obj).length) return '';
  return (
    '?' +
    Object.keys(obj)
      .filter((key) => obj[key])
      .map((key) => `${key}=${obj[key]}`)
      .join('&')
  );
}

export const hasNextPage = <Results>({
  pagination,
  results,
}: PaginatedResults<Results>): boolean => {
  if (pagination && results && results.length) {
    const { offset, totalResults } = pagination;
    const parsedOffset = Number(offset);
    return parsedOffset + results.length < totalResults;
  }
  return false;
};

export const getRequest = <Response>(path: string): Promise<Response> =>
  fetchWithToken<Response>(path);

export function nonNullable<T>(value: T): value is NonNullable<T> {
  return value !== null && value !== undefined;
}

export const listToTree = (arr: ActivityRow[]) => {
  let node, i;
  const newArr: ActivityRow[] = [],
    res = [];
  const map = new Map<string, number>();
  for (i = 0; i < arr.length; i += 1) {
    map.set(arr[i].activity.uniqueId, i);
    newArr[i] = { ...arr[i], subRows: [] };
  }
  for (i = 0; i < newArr.length; i += 1) {
    node = newArr[i];
    if (node.activity.parentId !== '0' && node.activity.parentId) {
      const newIndex = map.get(node.activity.parentId);
      if (newIndex !== undefined) {
        newArr[newIndex].subRows.push(node);
      }
    } else {
      res.push(node);
    }
  }
  return res;
};

/**
 * Find closest date ahead given day of the week.
 * @param from Date to start searching from.
 * @param day isoWeekday of day you want to search ahead. 1 is Monday, 7 is Sunday.
 */
export function findClosestAhead(from: Date, day: number): Date {
  const fromDay = getISODay(from);

  if (fromDay < day) {
    return setISODay(from, day);
  }
  const newDate = addDate(from, { weeks: 1 });
  return setISODay(newDate, day);
}

/**
 * Find closest date behind given day of the week.
 * @param from Date to start searching from.
 * @param day isoWeekday of day you want to search behind. 1 is Monday, 7 is Sunday.
 */
export function findClosestBehind(from: Date, day: number): Date {
  const fromDay = getISODay(from);
  if (fromDay >= day) {
    return setISODay(from, day);
  }
  const newDate = subDate(from, { weeks: 1 });
  return setISODay(newDate, day);
}

export function getQuickLookDate(filter: QuickLookFilters, increment: boolean) {
  if (increment) {
    return addDate(findClosestAhead(new Date(), IsoWeekday.MONDAY), {
      weeks: weekOffsetMap[filter],
    });
  }
  return subDate(findClosestBehind(new Date(), IsoWeekday.MONDAY), {
    weeks: weekOffsetMap[filter],
  });
}
